import React from 'react';

import { Typography } from '@mui/material';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { useConfigurationStore } from 'store/configurationsStore';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { LoaderSpinner } from 'components/common/LodaerSpinner/LoaderSpinner';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { ITableWidgetWrapperProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { IMAGE_UPLOADER_TAB_UPLOAD, useImageUploaderLogic } from 'components/widgets/ImageUploader/ImageUploader.logic';
import { useImageUploaderProvider } from 'components/widgets/ImageUploader/ImageUploader.provider';
import Dropzone from 'components/widgets/ImageUploader/component/Dropzone/Dropzone';
import { Image } from 'components/widgets/ImageUploader/component/Image/Image';
import ImageFromUrl from 'components/widgets/ImageUploader/component/ImageFromUrl/ImageFromUrl';
import { ImageUploaderTabs } from 'components/widgets/ImageUploader/component/ImageUploaderTabs/ImageUploaderTabs';

import classes from './ImageUploader.module.scss';

export const ImageUploader = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    externalComponents = null,
    setSubTitleForExport,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title: propTitle,
}: ITableWidgetWrapperProps) => {
    const { componentKey, routeId: id, endpoints, name: configTitle } = useConfigurationStore().image;

    const { ui, api } = useImageUploaderProvider({
        widgetId,
        endpoints,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title: propTitle ?? configTitle,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        onAddOrEditWidgetCallback: (widgetId) => {
            api.uploadImage.instance.mutate({ ...api.uploadImage.payload, widgetId });
        },
        apiPayload: api.getImage.payload,
        widgetEndpoint: endpoints.getImage,
    });

    const { logic } = useImageUploaderLogic({ api, ui });

    const getDComponent = () => {
        if (api.getImage.instance.isFetching) {
            return (
                <div className={classes.noImageUploadMessageWrapper}>
                    <LoaderSpinner className={classes.loader} />
                </div>
            );
        } else if (
            (WidgetViewModeUtils.isReportView(widgetViewMode) ||
                WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)) &&
            ui.state.imageUrl
        ) {
            return (
                <Image
                    imageUrl={ui.state.imageUrl}
                    imageFile={ui.state.imageFile}
                    showRemoveIcon={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                    onRemove={logic.handleClearImage}
                />
            );
        } else if (WidgetViewModeUtils.isReportView(widgetViewMode) && !ui.state.imageUrl) {
            return (
                <div className={classes.noImageUploadMessageWrapper}>
                    <Typography className={classes.noImageUploadMessage}>No Image Uploaded</Typography>
                </div>
            );
        } else if (WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && !ui.state.imageUrl) {
            return (
                <>
                    <ImageUploaderTabs value={ui.state.activeSourceTab} onChange={ui.setters.setActiveSourceTab} />
                    <div className={classes.panel}>
                        {ui.state.activeSourceTab === IMAGE_UPLOADER_TAB_UPLOAD ? (
                            <Dropzone onImageUpload={logic.handleFileFromDropzone} />
                        ) : (
                            <ImageFromUrl onLoadUrl={logic.handleUrlLoad} />
                        )}
                    </div>
                </>
            );
        }

        return null;
    };
    return (
        <WidgetWithTitle
            id={id}
            title={
                <WidgetTitle
                    title={editableTitle}
                    setTitle={setEditableTitle}
                    widgetViewMode={widgetViewMode}
                    showPostfixInEditMode={true}
                />
            }
            className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ExportDropdown key={'exportDropDown'} title={propTitle ?? configTitle} subTitle={subTitle} />
                ),
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ThreeDotsMenu
                        widgetViewMode={widgetViewMode}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        shareTitle={propTitle ?? configTitle}
                        shareLink={route}
                        projectId={projectId}
                        contractId={contractId}
                    />
                ),
            ]}
            titleFilters={[
                <ReportingVersionAndPills
                    key={'ReportingVersionAndPills'}
                    selectedProjectAndContract={widgetSelectedProjectAndContract}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
            ]}
        >
            <div className={classes.content}>{getDComponent()}</div>
        </WidgetWithTitle>
    );
};
